import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import CTABanner from '../components/CTABanner';
import { Button } from '../components/ui/button';
import { Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EmployersPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Employers - WorkNation Labour Ltd | Staffing Solutions BC</title>
        <meta name="description" content="Partner with WorkNation Labour Ltd for reliable staffing across BC. Construction, warehouse, agriculture, transportation, cold storage workers. WCB compliant, 24-48 hour placement. Request workers today." />
        <link rel="canonical" href="https://worknation.ca/employers" />
      </Helmet>
      <Header />
      <main>
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#003366] mb-6">Employers &mdash; Staffing Solutions Across British Columbia</h1>
            <p className="text-lg text-gray-700 mb-10 leading-relaxed max-w-3xl">
              Partner with WorkNation Labour Ltd for all your staffing needs. We provide reliable, WCB-compliant workers in <strong>construction, warehouse & fulfillment, food processing, agriculture, transportation, and cold storage</strong> across British Columbia. Over <strong>200 workers placed</strong> &mdash; let us solve your labour challenges.
            </p>

            <div className="bg-gray-50 p-8 rounded-xl mb-12">
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Why Employers Choose WorkNation</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Get workers on-site in 24-48 hours',
                  'Full WCB and WorkSafe BC compliance',
                  'Payroll and administration handled for you',
                  'Pre-screened, qualified candidates',
                  'Temp-to-perm trial options',
                  'Replacement guarantee if worker doesn\u2019t meet standards',
                  'Flexible daily, weekly, and contract placements',
                  'Coverage across all of British Columbia',
                ].map((item, i) => (
                  <div key={i} className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-0.5" size={20} aria-hidden="true" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#003366] mb-4">Industries We Serve</h2>
              <div className="flex flex-wrap gap-3">
                {[
                  { label: 'Construction', path: '/industries/construction' },
                  { label: 'Warehouse & Fulfillment', path: '/industries/warehouse' },
                  { label: 'Food Processing', path: '/industries/food-processing' },
                  { label: 'Agriculture', path: '/industries/agriculture' },
                  { label: 'Transportation', path: '/industries/transportation' },
                  { label: 'Cold Storage', path: '/industries/cold-storage' },
                ].map((ind) => (
                  <button
                    key={ind.path}
                    onClick={() => navigate(ind.path)}
                    className="px-4 py-2 bg-blue-50 text-[#003366] rounded-lg font-medium hover:bg-[#003366] hover:text-white transition-colors duration-200 flex items-center"
                  >
                    {ind.label} <ArrowRight className="ml-1" size={14} />
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-[#003366] mb-6 text-center">Request Workers Now</h2>
              <ContactForm type="employer" />
            </div>

            <div className="text-center bg-gray-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-[#003366] mb-4">Or Email Us Directly</h3>
              <a href="mailto:info@worknation.ca" className="inline-block">
                <Button className="bg-[#003366] hover:bg-[#004488] text-white px-8 py-6 text-lg transition-all duration-300" data-testid="employer-email-btn">
                  <Mail className="mr-2" size={20} />
                  info@worknation.ca
                </Button>
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EmployersPage;
